import { useEffect, useState, useRef } from "react";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../lib/api";

export default function HeroSlideshow() {
  const [slides, setSlides] = useState([]);
  const [settings, setSettings] = useState({
    default_duration: 4,
    transition_duration: 0.8,
    autoplay: true,
    pause_on_hover: true,
    show_arrows: true,
    show_dots: true,
  });
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(0);

  useEffect(() => {
    api.get("/slideshow").then((r) => {
      setSlides(r.data.slides || []);
      setSettings({ ...settings, ...(r.data.settings || {}) });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!slides.length || !settings.autoplay || paused) return;
    const slide = slides[current];
    const dur = (slide?.duration_override || settings.default_duration || 4) * 1000;
    const t = setTimeout(() => setCurrent((c) => (c + 1) % slides.length), dur);
    return () => clearTimeout(t);
  }, [current, slides, settings, paused]);

  const go = (i) => setCurrent(((i % slides.length) + slides.length) % slides.length);

  const onTouchStart = (e) => (touchStartX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(diff) > 50) go(current + (diff < 0 ? 1 : -1));
  };

  if (!slides.length) {
    return (
      <section className="relative h-[560px] bg-navy flex items-center" data-testid="hero-empty">
        <div className="container-site text-white">
          <p className="accent-script text-gold text-2xl mb-2">Otel Açılışınız İçin</p>
          <h1 className="heading-serif text-4xl md:text-6xl font-semibold leading-tight max-w-2xl">
            A'dan Z'ye Uzman Ekipman Danışmanlığı
          </h1>
        </div>
      </section>
    );
  }

  return (
    <section
      className="relative w-full h-[540px] md:h-[620px] lg:h-[680px] overflow-hidden bg-navy grain-overlay"
      onMouseEnter={() => settings.pause_on_hover && setPaused(true)}
      onMouseLeave={() => settings.pause_on_hover && setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="hero-slideshow"
    >
      {slides.map((s, i) => (
        <div
          key={s.id || i}
          className={`absolute inset-0 transition-opacity duration-[800ms] ease-in-out ${
            i === current ? "opacity-100 z-10" : "opacity-0 z-0"
          }`}
          data-testid={`hero-slide-${i}`}
        >
          {s.media_type === "video" && s.video_url ? (
            <video
              className="h-full w-full object-cover"
              src={s.video_url}
              autoPlay
              muted
              loop
              playsInline
              poster={s.fallback_image || s.image_desktop}
            />
          ) : (
            <img
              src={s.image_desktop}
              alt={s.title_tr}
              className="h-full w-full object-cover"
              loading={i === 0 ? "eager" : "lazy"}
            />
          )}
          {/* Overlay */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(90deg, ${s.overlay_color || "#08111d"}${Math.round(
                (s.overlay_opacity ?? 0.55) * 255
              ).toString(16).padStart(2, "0")} 0%, ${s.overlay_color || "#08111d"}20 100%)`,
            }}
          />

          {/* Content */}
          <div className="absolute inset-0 flex items-center">
            <div className="container-site text-white max-w-3xl">
              <p className="accent-script text-gold text-xl md:text-2xl mb-3 opacity-0 animate-fade-in [animation-delay:120ms]">
                Uzman Danışmanınız
              </p>
              <h1 className="heading-serif text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.07] tracking-tight mb-5 opacity-0 animate-fade-in [animation-delay:220ms]">
                {s.title_tr}
              </h1>
              {s.subtitle_tr && (
                <p className="text-white/85 text-base md:text-lg max-w-xl mb-8 leading-relaxed opacity-0 animate-fade-in [animation-delay:340ms]">
                  {s.subtitle_tr}
                </p>
              )}
              <div className="flex flex-wrap gap-3 opacity-0 animate-fade-in [animation-delay:460ms]">
                {s.cta1_text && (
                  <Link
                    to={s.cta1_link || "/"}
                    className={s.cta1_style === "outline" ? "btn-outline-white" : s.cta1_style === "teal" ? "btn-teal" : "btn-gold"}
                    data-testid={`hero-cta1-${i}`}
                  >
                    {s.cta1_text} <ArrowRight size={16} />
                  </Link>
                )}
                {s.cta2_text && (
                  <Link
                    to={s.cta2_link || "/"}
                    className={s.cta2_style === "gold" ? "btn-gold" : s.cta2_style === "teal" ? "btn-teal" : "btn-outline-white"}
                    data-testid={`hero-cta2-${i}`}
                  >
                    {s.cta2_text}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}

      {settings.show_arrows && slides.length > 1 && (
        <>
          <button
            className="slide-arrow absolute left-4 md:left-6 top-1/2 -translate-y-1/2 z-20"
            onClick={() => go(current - 1)}
            aria-label="Önceki slayt"
            data-testid="hero-prev"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            className="slide-arrow absolute right-4 md:right-6 top-1/2 -translate-y-1/2 z-20"
            onClick={() => go(current + 1)}
            aria-label="Sonraki slayt"
            data-testid="hero-next"
          >
            <ChevronRight size={18} />
          </button>
        </>
      )}

      {settings.show_dots && slides.length > 1 && (
        <div className="absolute bottom-8 left-0 right-0 z-20 flex justify-center gap-2" data-testid="hero-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`slide-dot ${i === current ? "active" : ""}`}
              onClick={() => go(i)}
              aria-label={`Slayt ${i + 1}`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
